import ld from 'lodash';
import moment from 'moment-timezone';
import { LAX, NYC, CHI } from 'constants/Markets';
import ROLES from 'constants/UserRoles';
import ShipmentsActionCreators from 'actions/ShipmentsActionCreators';
import originalAccessorialMap from 'config/accessorials';
import DocumentUploadStore from 'stores/DocumentUploadStore';
import SessionStore from 'stores/SessionStore';
import _ from 'underscore';
import { humanize } from 'underscore.string';
export const accessorialMap = {};
Object.keys(originalAccessorialMap).forEach((a) => {
    if (originalAccessorialMap[a].include_in_booking) {
        accessorialMap[a] = Object.assign({}, originalAccessorialMap[a]);
    }
});
function getDefaultPickupWindow(timezone) {
    const now = moment();
    let pickupNow = now.clone();
    if (timezone) {
        pickupNow = pickupNow.tz(timezone);
    }
    const minutes = 15 - (pickupNow.minutes() % 15);
    // round time to nearest 15 minute mark
    pickupNow = pickupNow.add('minutes', minutes);
    const pickupStart = pickupNow.add('minutes', 30);
    const pickup_after = pickupStart.toISOString();
    const pickup_by = pickupStart.add('hours', 4).toISOString();
    return {
        pickup_after,
        pickup_by,
    };
}
function getDefaultDeliverWindow(timezone) {
    const now = moment();
    let deliverNow = now.clone();
    if (timezone) {
        deliverNow = deliverNow.tz(timezone);
    }
    const deliverStart = deliverNow.add('minutes', (30 - (now.minute() % 30)));
    const deliver_after = deliverStart.add('hours', 4).toISOString();
    const deliver_by = deliverStart.add('hours', 4).toISOString();
    return {
        deliver_after,
        deliver_by,
    };
}
export function getLocationUpdates(location) {
    const { address = '', name = '', lat, lng, suite_or_apt = '', } = location;
    const market = location.market || {};
    const timezone = market.timezone || 'America/Los_Angeles';
    const contact = location.contact || {};
    const contactName = contact.name || '';
    const contactPhone = contact.phone || '';
    const updates = {
        name: name || '',
        address: {
            label: address || '',
        },
        suite_or_apt: suite_or_apt || '',
        contact: {
            name: contactName,
            phone: contactPhone,
        },
        timezone,
    };
    if (lat && lng) {
        updates.address.location = {
            lat,
            lng,
        };
    }
    return updates;
}
function getInitialLocations() {
    const defaultValue = {
        address: {},
        contact: {
            name: '',
            phone: '',
        },
        name: '',
        suite_or_apt: '',
    };
    let origin = ld.cloneDeep(defaultValue);
    let destination = ld.cloneDeep(defaultValue);
    if (SessionStore.isShipper()) {
        const user = SessionStore.getUser();
        if (user.origin) {
            origin = getLocationUpdates(user.origin);
        }
        if (user.destination) {
            destination = getLocationUpdates(user.destination);
        }
    }
    return {
        origin,
        destination,
    };
}
function getInitialMarket() {
    const user = SessionStore.getUser();
    if (user._type === ROLES.ADMIN || !user.origin) {
        return LAX.value;
    }
    return user.origin.market.code;
}
export function getStepNumbers() {
    const isShipper = SessionStore.isShipper();
    if (isShipper) {
        return {
            shipperSelection: 0,
            originDestination: 1,
            loadDetails: 2,
            services: 3,
            finalize: 4,
        };
    }
    return {
        shipperSelection: 1,
        originDestination: 2,
        loadDetails: 3,
        services: 4,
        finalize: 5,
    };
}
export function getInitialState() {
    const accessorialKeys = Object.keys(accessorialMap);
    const accessorials = accessorialKeys.reduce((prev, curr) => {
        const next = prev;
        next[curr] = false;
        return next;
    }, {});
    const user = SessionStore.getUser() || {};
    const isShipper = SessionStore.isShipper();
    const shipper_id = isShipper ? user._id : null;
    const step = 1;
    const hideCustomerDetails = isShipper;
    const shipper = isShipper ? user : null;
    // Used to prevent shippers from accessing booking flow
    const hasAccountingHold = (isShipper && user.accounting) ? user.accounting.on_hold : false;
    return Object.assign(Object.assign(Object.assign({ hasAccountingHold, showAccountingHoldModal: hasAccountingHold, offer: null, delivery_order: '', accessorials, boxes: {}, cost: 0.0, courier: false, distance: 0.0, error: '', hold_at_dispatch: false, house_bill_number: '', tracking_id: '', notes: '', market: getInitialMarket(), mo_number: '', load_changed: false, pallets: [{
                quantity: 1,
                length: 48,
                width: 40,
                height: 60,
            }], business: false, project: false, shipper, shipper_id: shipper_id, full_truckload: false, equipment_type: [], weight: '', shipment_type: 'shorthaul', completedSteps: {
            shipperSelection: isShipper,
            locationDetails: false,
            schedulingDetails: false,
        }, hideCustomerDetails,
        step, mapHeight: 500, errors: {}, booking: false, exception_exempt: false, fetchingDistance: false, selectedOriginTimezone: null, userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone, locations: [], using_quote: false }, getInitialLocations()), getDefaultPickupWindow()), getDefaultDeliverWindow());
}
export function getLoad() {
    return {
        height: 0,
        length: 0,
        quantity: 0,
        width: 0,
    };
}
export function getAccessorialDocuments(key) {
    const documents = [];
    const doDoc = DocumentUploadStore.getDocument(`${key}-do`);
    if (doDoc) {
        documents.push(doDoc);
    }
    else {
        throw new Error(`${humanize(key)} Delivery Order Required`);
    }
    const atmeDoc = DocumentUploadStore.getDocument(`${key}-atme`);
    if (atmeDoc)
        documents.push(atmeDoc);
    return documents;
}
export function getMapOptions() {
    return {
        styles: [{ stylers: [{ gamma: 0.8 }, { lightness: 15 }, { visibility: 'on' }] }],
    };
}
export function handleFileUpload(key) {
    // Composite key is later used to extract the document from the store
    return (file, type) => {
        ShipmentsActionCreators.uploadBookingDocument(`${key}-${type}`, type, file);
    };
}
export function isValid(error) {
    if (!error) {
        return false;
    }
    if (Object.keys(error).length === 0) {
        return true;
    }
    return !Object.keys(error).find(key => (!_.isArray(error[key]) && Object.keys(error[key]).length > 0) || !isValid(error[key]));
}
export function getAbbreviatedTimezone(zone) {
    return moment.tz(zone).zoneAbbr();
}
// Deprecated. It can be removed when we get rid of v1 booking and
// ManageStagingItem is migrated to use utils/Timezones like booking v2 does.
export function getMarkets() {
    return [
        Object.assign(Object.assign({}, LAX), { abbr: getAbbreviatedTimezone(LAX.timezone) }),
        Object.assign(Object.assign({}, CHI), { abbr: getAbbreviatedTimezone(CHI.timezone) }),
        Object.assign(Object.assign({}, NYC), { abbr: getAbbreviatedTimezone(NYC.timezone) })
    ];
}
